import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toaster'
import { api } from '@/lib/api'
import { ArrowLeft, FileText, Loader2, Link as LinkIcon } from 'lucide-react'
import {
  ManualProductEditor,
  type ManualProductDraft,
} from '@/components/products/ManualProductEditor'
import { ProductImportFromUrlCard } from '@/components/products/ProductImportFromUrlCard'

const EMPTY_MANUAL_PRODUCT: ManualProductDraft = {
  product_name: '',
  description: '',
  selling_points: [],
  target_market: '',
  target_audience: '',
  price: '',
  language: 'en',
  keywords: [],
  competitor_brands: [],
}

export function ProductCreatePage() {
  const navigate = useNavigate()
  const [mode, setMode] = useState<'url' | 'manual'>('url')
  const [url, setUrl] = useState('')
  const [formData, setFormData] = useState<ManualProductDraft>(EMPTY_MANUAL_PRODUCT)

  // Create from URL
  const createFromUrlMutation = useMutation({
    mutationFn: async (url: string) => {
      const response = await api.createProductFromUrl(url)
      return response
    },
    onSuccess: (data) => {
      toast({ title: 'Success', description: 'Product imported successfully' })
      navigate(`/products/${data.id}`)
    },
    onError: (error: any) => {
      // Skip if 401 - already handled by global interceptor
      if (error.response?.status === 401) return
      toast({
        title: 'Failed to parse URL',
        description: error.response?.data?.error?.message || 'Please check if the URL is valid and try again',
        variant: 'destructive',
      })
    },
  })

  // Create manually
  const createManualMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      // Convert selling_points array to string for API
      const submitData = {
        ...data,
        selling_points: data.selling_points.join('\n'),
      }
      return api.createProduct(submitData)
    },
    onSuccess: (data) => {
      toast({ title: 'Success', description: 'Product created successfully' })
      navigate(`/products/${data.id}`)
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to create product',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive',
      })
    },
  })

  const handleUrlSubmit = () => {
    if (url) {
      createFromUrlMutation.mutate(url)
    }
  }

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createManualMutation.mutate(formData)
  }

  return (
    <div className="space-y-6">
      {/* Loading Overlay for URL Parsing */}
      {createFromUrlMutation.isPending && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 text-center max-w-sm mx-4">
            <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
            <p className="font-medium text-lg">Parsing product information ...</p>
            <p className="text-sm text-gray-500 mt-2">This usually takes 30-60 seconds</p>
          </div>
        </div>
      )}

      {/* Back Button */}
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/products')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Mode Toggle */}
      <div className="flex gap-2 p-1 bg-gray-100 rounded-lg w-fit">
        <button
          onClick={() => setMode('url')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            mode === 'url'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <LinkIcon className="w-4 h-4 inline mr-2" />
          From URL
        </button>
        <button
          onClick={() => setMode('manual')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            mode === 'manual'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <FileText className="w-4 h-4 inline mr-2" />
          Manual Entry
        </button>
      </div>

      {/* URL Mode */}
      {mode === 'url' && (
        <ProductImportFromUrlCard
          url={url}
          onUrlChange={setUrl}
          onSubmit={handleUrlSubmit}
          isPending={createFromUrlMutation.isPending}
          isError={createFromUrlMutation.isError}
        />
      )}

      {/* Manual Mode */}
      {mode === 'manual' && (
        <form onSubmit={handleManualSubmit} className="space-y-6">
          <ManualProductEditor value={formData} onChange={setFormData} disabled={createManualMutation.isPending} />

          {createManualMutation.isError && (
            <p className="text-red-500 text-sm">Failed to create product. Please try again.</p>
          )}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate('/products')}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!formData.product_name || createManualMutation.isPending}
            >
              {createManualMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Create Product
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}
